import type { UpdateDeliveryItemDto } from '@shared/dto/delivery';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../../hooks/analytics/use-analytics';
import { lotStorageQueryKeys } from '../../../hooks/lot-storage/query-keys';
import { useNotification } from '../../../hooks/use-notification';
import type {
  AddItemDto,
  IGoodsReceivingService,
  UpdateItemStatusDto,
  ValidateItemDto,
} from '../../../services/goods-receiving';
import { GoodsReceivingService } from '../../../services/goods-receiving';
import { getSimilarSuppliersConflict } from '../../../services/suppliers/similar-suppliers';
import { resolveApiErrorMessage } from '../../../utils/error.utils';
import { supplierQueryKeys } from '../../suppliers/query-keys';
import { goodsReceivingQueryKeys } from '../query-keys';

export function useCloseDeliveryNote() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: async (id: string) => {
      return service.closeDeliveryNote(id);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.noteDetail(data.id),
      });
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.notes(),
      });
      // Closing a delivery note persists LotStorageAssignment rows server-side
      // (commit 4c8e8b0ae). Invalidate the lot-storage cache so the equipment
      // and cleaning-area "Cosa contiene" views refetch the newly-assigned lots.
      queryClient.invalidateQueries({
        queryKey: lotStorageQueryKeys.all,
      });
      trackEvent('ddt_close', { item_count: data.items?.length ?? 0 });
      showSuccess(t('closeSuccess'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useValidateItem() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: ValidateItemDto }) => {
      return service.validateItem(itemId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.notes(),
      });
      trackEvent('ddt_item_validate', { result: 'conformant' });
      showSuccess(t('validateItemSuccess'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useUpdateItemStatus() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: UpdateItemStatusDto }) => {
      return service.updateItemStatus(itemId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.notes(),
      });
      showSuccess(t('updateItemStatusSuccess'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useUploadLabelPhoto() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: async ({ itemId, file }: { itemId: string; file: File }) => {
      return service.uploadLabelPhoto(itemId, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.notes(),
      });
      showSuccess(t('uploadPhotoSuccess'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useUpdateDeliveryItem() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: async ({
      itemId,
      data,
    }: {
      itemId: string;
      data: UpdateDeliveryItemDto;
      deliveryNoteId?: string;
    }) => {
      return service.updateItem(itemId, data);
    },
    onSuccess: async (_, variables) => {
      if (variables.deliveryNoteId) {
        await queryClient.invalidateQueries({
          queryKey: goodsReceivingQueryKeys.draft(variables.deliveryNoteId),
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: goodsReceivingQueryKeys.all,
        });
      }
      showSuccess(t('updateItemSuccess'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

/**
 * Marks a batch of items as "no expiry" (shelf-stable) in one go. Used when the
 * user confirms they accept to ignore the missing expiry dates on the items
 * list: the backend rejects completion of items with neither an expiry date nor
 * hasNoExpiry, so recording the consent as hasNoExpiry=true keeps the receipt
 * completable without weakening the server-side HACCP invariant. Single draft
 * invalidation, no per-item success toast.
 */
export function useMarkItemsNoExpiry() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: async ({
      itemIds,
      deliveryNoteId,
    }: {
      itemIds: string[];
      deliveryNoteId: string;
    }) => {
      await Promise.all(itemIds.map((itemId) => service.updateItem(itemId, { hasNoExpiry: true })));
      return deliveryNoteId;
    },
    onSuccess: async (deliveryNoteId) => {
      await queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.draft(deliveryNoteId),
      });
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useCreateSupplierFromGoodsReceiving() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('suppliers');

  return useMutation({
    mutationFn: async (data: Parameters<IGoodsReceivingService['createSupplier']>[0]) => {
      return service.createSupplier(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goodsReceivingQueryKeys.suppliers() });
      queryClient.invalidateQueries({ queryKey: supplierQueryKeys.all });
      showSuccess(t('createSuccess'));
    },
    onError: (err: unknown) => {
      // The similar-suppliers 409 conflict is handled inline by SupplierFormDialog
      // (opens SimilarSupplierDialog) — skip the generic error toast for it.
      if (getSimilarSuppliersConflict(err)) {
        return;
      }
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useAddItem() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: async ({
      deliveryNoteId,
      item,
      files,
    }: {
      deliveryNoteId: string;
      item: AddItemDto;
      files?: File[];
    }) => {
      return service.addItem(deliveryNoteId, item, files);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.draft(variables.deliveryNoteId),
      });
      showSuccess(t('itemAdded'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}

export function useDeleteItem() {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  return useMutation({
    mutationFn: async ({ itemId, draftId }: { itemId: string; draftId: string }) => {
      await service.deleteItem(itemId);
      return draftId;
    },
    onSuccess: (draftId) => {
      queryClient.invalidateQueries({
        queryKey: goodsReceivingQueryKeys.draft(draftId),
      });
      showSuccess(t('itemRemoved'));
    },
    onError: (err: unknown) => {
      showError(resolveApiErrorMessage(err, t));
    },
  });
}
